import * as React from 'react'
import {
  ICoordinates,
  IStoreState,
  IRiderObject,
  IRiderLocationObject,
  IStateMetadata,
  IServiceLocationObject
} from '../types'
import { IGoogleMapBoundsDTO, ICoordinatesWithShortKeyDTO } from '../types/google-map'
import { searchLocation, getPlaceDetails } from '../utils/location'
import UIMessages from '../utils/ui-messages'
import Modal from './ui/Modal'
import { googleMapService } from '../services/GoogleMap'
import MapView from '../components/MapView'
import LocationItem from '../components/riders/common/LocationItem'
import { displacementBetween } from '../utils/location'

export interface IPinLocationModalProps {
  zoom: number
  bounds: IGoogleMapBoundsDTO
  locations: IRiderLocationObject[] | IServiceLocationObject[]
  location: IRiderLocationObject | IServiceLocationObject
  locationType?: string
  metadata?: IStateMetadata
  onCancel: () => void
  saveLocation: (location: IRiderLocationObject | IServiceLocationObject) => void
  updateLocation?: (location: IRiderLocationObject | IServiceLocationObject) => void
}

interface IPinLocationModalState {
  isSaving: boolean
  currentError: { isError: boolean; message: string }
  location: any
  searchedCoordinates: ICoordinatesWithShortKeyDTO
  autoCompleteLocations: IRiderLocationObject[] | IServiceLocationObject[]
}

interface IProps {
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}
//Declaration of radius (CIRCLE)
const LOCATION_RANGE = 2500 // Maximum drag distance in meters

const BottomBar = ({ onCancel, isSaving, onSave }: IProps) => {
  return (
    <div className='flex col-12 flex bg-light-grey2 py1'>
      <div style={{ flex: 1 }} className='flex-row flex justify-end mr2'>
        <button
          type='button'
          className='SaveButton inline-block btn border-white font-dark-grey rounded font-white weight-300 mt-auto mb-auto mr4'
          onClick={onCancel}
        >
          {UIMessages.common.close}
        </button>
        <button
          type='button'
          className='SaveButton inline-block btn bg-green rounded font-white weight-300 mt-auto mb-auto'
          onClick={onSave}
        >
          {isSaving ? UIMessages.common.saving : UIMessages.common.save}
        </button>
      </div>
    </div>
  )
}

class PinLocationModal extends React.Component<IPinLocationModalProps, IPinLocationModalState> {
  public state = {
    isSaving: false,
    location: { ...this.props.location },
    searchedCoordinates: { ...this.props.location.coordinates },
    currentError: { isError: false, message: '' },
    autoCompleteLocations: []
  }

  public componentDidUpdate(prevProps: IPinLocationModalProps) {
    if (prevProps.metadata !== this.props.metadata) {
      if (this.props.metadata && this.props.metadata.processingError) {
        this.showMessage(this.props.metadata.processingError, true)
      }
      if (this.props.metadata && this.props.metadata.processingSuccess) {
        this.showMessage(this.props.metadata.processingSuccess, false)
      }
    }
  }

  public render() {
    const { message, isError } = this.state.currentError

    return (
      <Modal>
        <div className='fixed top-0 bottom-0 left-0 right-0 flex justify-center items-center'>
          <div className='col-8 bg-white'>
            {message.length > 0 && (
              <div className={'pr1 pl3 pt1 pb1 font-white ' + (isError ? 'bg-red' : 'bg-green')}>{message}</div>
            )}
            <div className='px2 pt2 map-container'>
              <MapView
                origin={null}
                destination={null}
                center={this.state.location.coordinates}
                zoom={this.props.zoom}
                polyline={null}
                bounds={this.props.bounds}
                renderStrategyStage=''
                location={this.state.location}
                selectLocation={this.selectLocation}
                circles={this.circleDetails()}
                isModal={true}
                waypoints={null}
                isDraggable={true}
                zones={{}}
              />
            </div>
            <div className='mt1'>
              <LocationItem
                index={-1}
                allowToEdit={true}
                location={this.state.location}
                locations={this.props.locations}
                autoCompleteLocations={this.state.autoCompleteLocations}
                onLocationSelect={this.onLocationItemSelect}
                searchLocation={this.searchLocation}
                showMessage={this.showMessage}
                updateLocation={this.onUpdateLocation}
              />
            </div>
            <BottomBar onCancel={this.props.onCancel} isSaving={this.state.isSaving} onSave={this.onSave} />
          </div>
        </div>
      </Modal>
    )
  }

  private circleDetails = () => [
    {
      center: this.state.searchedCoordinates,
      radius: LOCATION_RANGE,
      defaultOptions: {
        fillOpacity: 0.0,
        fillColor: ''
      }
    }
  ]

  private selectLocation = async (coordinates: ICoordinatesWithShortKeyDTO) => {
    const dwLocations = displacementBetween(this.state.searchedCoordinates, coordinates)
    const { location } = this.state
    if (dwLocations <= LOCATION_RANGE) {
      location.coordinates = { ...coordinates }
    } else {
      location.coordinates = { ...location.coordinates }
    }

    this.setState({
      location: { ...location }
    })
  }

  private onUpdateLocation = (location: any) => {
    if (this.props.updateLocation) {
      this.props.updateLocation(location)
    }
    this.setState({
      location
    })
  }

  private onSave = () => {
    const { location, currentError } = this.state
    if (currentError.isError) {
      return
    }
    if (!location.fullAddress || location.fullAddress.length < 3) {
      this.showMessage(UIMessages.rider.validation.locationAddressMinLength, true)
      return
    }
    this.showMessage('', false)
    this.setState({ isSaving: true })
    this.props.saveLocation(location)
  }

  private searchLocation = async (address: string, index: number) => {
    const { location } = this.state
    location.fullAddress = address
    if (!address) {
      this.setState({
        location,
        autoCompleteLocations: []
      })
      return
    }
    const autoCompleteLocations = await searchLocation({ input: address })
    this.setState({
      location,
      autoCompleteLocations
    })
  }

  private onLocationItemSelect = async (selectedLocation: any) => {
    const { googlePlaceId } = selectedLocation

    const data = await getPlaceDetails(googlePlaceId)
    const location = { ...selectedLocation, ...data }
    //if user typed a name before address earch, keep it
    location.placeName = selectedLocation.placeName || location.placeName
    this.setState({ searchedCoordinates: location.coordinates, location, autoCompleteLocations: [] })
  }

  private showMessage = (message: string, isError: boolean) => {
    if (this.state.currentError.message === message) {
      return
    }
    this.setState({ currentError: { message, isError }, isSaving: false })
  }
}

export default PinLocationModal



// WEBPACK FOOTER //
// ./src/components/PinLocationModal.tsx